import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isIOS, isAndroid } from "@/lib/platform";
import { Share, PlusSquare, MoreVertical, Download, Smartphone, Zap } from "lucide-react";

/**
 * Post-logout install nudge for mobile users.
 * Renders iOS + Android instructions with icons, plus a Skip link back to the
 * landing page. Only routed to when the user signs out on a mobile device that
 * isn't already running the app as an installed PWA.
 *
 * When the query param `?logout=1` is present, the session is cleared on mount
 * — but only AFTER the screen has rendered, so the user never sees a flash of
 * the landing page during sign-out.
 */
export default function InstallScreen() {
  const nav = useNavigate();
  const { logout, user } = useAuth();
  const [params] = useSearchParams();
  const ios = isIOS();
  const android = isAndroid();

  // Clear the session on mount if the sign-out flow routed us here.
  useEffect(() => {
    if (params.get("logout") === "1" && user) {
      logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skip = () => {
    // Belt-and-braces — clear again in case the effect above was skipped.
    if (user) logout();
    nav("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-obsidian text-paper flex items-center justify-center px-5 py-10" data-testid="install-screen">
      <div className="w-full max-w-md">
        {/* Brand mark */}
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-acid uppercase mb-3">
          <Zap className="w-3 h-3" /> NITEWIRE · Install
        </div>

        <h1 className="font-heading text-4xl md:text-5xl leading-none">
          KEEP <span className="text-acid">NITEWIRE</span> ONE TAP AWAY
        </h1>
        <p className="font-mono text-xs text-white/60 mt-3 leading-relaxed">
          Add it to your home screen for instant access, push notifications, and a full-screen chat experience.
        </p>

        {/* iOS instructions — always show, prioritized if user is on iOS */}
        <section
          className={`mt-6 border-2 ${ios ? "border-acid" : "border-white/15"} bg-panel/40 p-4`}
          data-testid="install-ios"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] tracking-widest text-white/70 uppercase">iPhone · iPad</div>
            {ios && <div className="font-mono text-[10px] text-acid uppercase">// your device</div>}
          </div>
          <Step
            icon={<Smartphone className="w-4 h-4" />}
            text="Open NITEWIRE in Safari (not Chrome or in-app browsers)"
          />
          <Step
            icon={<Share className="w-4 h-4" />}
            text={<>Tap the <b>Share</b> icon at the bottom of the screen</>}
          />
          <Step
            icon={<PlusSquare className="w-4 h-4" />}
            text={<>Choose <b>Add to Home Screen</b></>}
          />
          <Step
            icon={<Download className="w-4 h-4" />}
            text={<>Tap <b>Add</b> in the top-right corner</>}
            last
          />
        </section>

        {/* Android instructions */}
        <section
          className={`mt-4 border-2 ${android ? "border-acid" : "border-white/15"} bg-panel/40 p-4`}
          data-testid="install-android"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] tracking-widest text-white/70 uppercase">Android</div>
            {android && <div className="font-mono text-[10px] text-acid uppercase">// your device</div>}
          </div>
          <Step
            icon={<Smartphone className="w-4 h-4" />}
            text="Open NITEWIRE in Chrome"
          />
          <Step
            icon={<MoreVertical className="w-4 h-4" />}
            text={<>Tap the <b>three-dot menu</b> in the top-right</>}
          />
          <Step
            icon={<Download className="w-4 h-4" />}
            text={<>Choose <b>Install app</b> or <b>Add to Home screen</b></>}
            last
          />
        </section>

        <button
          type="button"
          onClick={skip}
          data-testid="install-skip"
          className="mt-6 w-full font-mono text-xs tracking-widest text-white/60 hover:text-white uppercase py-3 border-2 border-white/15 hover:border-white/40 transition-colors"
        >
          Skip — back to landing
        </button>
      </div>
    </div>
  );
}

function Step({ icon, text, last = false }) {
  return (
    <div className={`flex items-start gap-3 py-2 ${last ? "" : "border-b border-white/5"}`}>
      <div className="w-8 h-8 flex items-center justify-center border-2 border-white/15 text-acid shrink-0">
        {icon}
      </div>
      <div className="font-body text-sm text-white/85 flex-1 pt-1">{text}</div>
    </div>
  );
}
